import { axiosInstance } from "@/lib/axios";

/**
 * A PROFILE'S PEOPLE — `GET /artists/{username}/followers` and `/following`. Public (a token,
 * when sent, fills `follow_state` for the viewer), newest follow first, no sort. Each person is
 * the small `/artists` card; galleries followed are not in either list. A missing, disabled or
 * admin account answers 404.
 */

export type FollowListKind = "followers" | "following";

/** `FOLLOWED` / `NOT_FOLLOWED` for the viewer (a guest always reads NOT_FOLLOWED); `CAN_NOT_FOLLOW` is the viewer's own card. */
export type FollowListState = "FOLLOWED" | "NOT_FOLLOWED" | "CAN_NOT_FOLLOW";

export type FollowPerson = {
  id: number;
  name: string | null;
  username: string;
  /** `cover` is the portrait (a default one when there is none); `banner` may be null. */
  profile?: { cover?: string | null; banner?: string | null } | null;
  /** `following` — how many this person follows; `follower` — how many follow them. */
  following?: { following?: number; follower?: number } | null;
  follow_state?: FollowListState | string | null;
};

export type FollowListPage = {
  artists: FollowPerson[];
  page: { current_page: number; last_page: number; per_page: number; total: number };
};

export const FOLLOW_LIST_PAGE_SIZE = 24;

export async function getFollowList(username: string, kind: FollowListKind, page: number, perPage = FOLLOW_LIST_PAGE_SIZE): Promise<FollowListPage> {
  const { data } = await axiosInstance.get<{ data: FollowListPage }>(
    `/artists/${encodeURIComponent(username)}/${kind}`,
    { params: { page, per_page: perPage }, suppressToast: true },
  );
  return data.data;
}

/** A card after the viewer followed (or unfollowed) that person: the state, and their follower count one up (or down). */
export function applyFollowChange(p: FollowPerson, next: "FOLLOWED" | "NOT_FOLLOWED"): FollowPerson {
  const was = p.follow_state === "FOLLOWED";
  const now = next === "FOLLOWED";
  if (was === now) return { ...p, follow_state: next };
  const follower = Math.max(0, (p.following?.follower ?? 0) + (now ? 1 : -1));
  return { ...p, follow_state: next, following: { ...p.following, follower } };
}
